import Tool, { type ToolArgs } from '../../../tools/Tool';
import { Editor, Range, Transforms, Node, type Path, Point } from 'slate';
import BlockUtils from '../../Blocks/BlockUtils';
import { isCodeBlockElement } from '../../../types/guards';

interface LineInfo {
  lineIndex: number;
  startPoint: Point;
  endPoint: Point;
  content: string;
}

// Extract line information from a code block, handling both text nodes and inline elements.
function getCodeBlockLines(editor: Editor, codeBlockPath: Path): LineInfo[] {
  const lines: LineInfo[] = [];
  let currentLineContent = '';
  let currentLineStart: Point | null = null;
  let currentLineIndex = 0;
  let lineStartCandidates: Point[] = [];

  const textNodes = Node.texts(editor, {
    from: codeBlockPath,
    to: codeBlockPath,
  });

  // Iterate through all text nodes in the code block
  for (const [textNode, textPath] of textNodes) {
    const textContent = textNode.text;

    for (let i = 0; i < textContent.length; i++) {
      const char = textContent[i];
      const currentPoint: Point = { path: textPath, offset: i };

      // Track potential line start positions (including empty text nodes)
      if (currentLineStart === null) {
        // Add this position as a candidate for line start
        lineStartCandidates.push(currentPoint);
        currentLineStart = currentPoint;
      }

      if (char === '\n') {
        // End of line - create LineInfo
        const lineEndPoint: Point = { path: textPath, offset: i };

        // Use the earliest candidate as the line start for indentation
        const actualLineStart =
          lineStartCandidates.length > 0
            ? lineStartCandidates[0]
            : currentLineStart;

        lines.push({
          lineIndex: currentLineIndex,
          startPoint: actualLineStart,
          endPoint: lineEndPoint,
          content: currentLineContent,
        });

        // Reset for next line
        currentLineContent = '';
        currentLineStart = null;
        lineStartCandidates = [];
        currentLineIndex++;

        // Check if the next character is also a newline (empty line case)
        if (i + 1 < textContent.length && textContent[i + 1] === '\n') {
          // We have an empty line - create a line entry for it
          const emptyLineStart: Point = { path: textPath, offset: i + 1 };
          const emptyLineEnd: Point = { path: textPath, offset: i + 1 };

          lines.push({
            lineIndex: currentLineIndex,
            startPoint: emptyLineStart,
            endPoint: emptyLineEnd,
            content: '',
          });

          currentLineIndex++;
          // Skip the next newline since we've already processed it
          i++;
        }
      } else {
        currentLineContent += char;
      }
    }

    // If this is an empty text node and we're at the start of a line, add it as a candidate
    // This handles cases where a line starts with an inline element
    if (textContent === '' && currentLineStart === null) {
      lineStartCandidates.push({ path: textPath, offset: 0 });
    }
  }

  // Handle the last line if it doesn't end with a newline
  if (currentLineStart !== null || lineStartCandidates.length > 0) {
    // Find the last text node to get the end point
    const lastTextEntry = Array.from(
      Node.texts(editor, { from: codeBlockPath, to: codeBlockPath })
    ).pop();
    if (lastTextEntry) {
      const [lastTextNode, lastTextPath] = lastTextEntry;
      const endPoint: Point = {
        path: lastTextPath,
        offset: lastTextNode.text.length,
      };

      // Use the earliest candidate as the line start
      const actualLineStart =
        lineStartCandidates.length > 0
          ? lineStartCandidates[0]
          : currentLineStart!;

      lines.push({
        lineIndex: currentLineIndex,
        startPoint: actualLineStart,
        endPoint: endPoint,
        content: currentLineContent,
      });
    }
  }

  return lines;
}

function applyIndent(editor: Editor, direction: 'indent' | 'outdent'): void {
  if (!editor) return;
  if (!editor.selection) return;

  // Find the code block containing the selection
  const codeBlockEntry = Editor.above(editor, {
    match: (n) => isCodeBlockElement(n),
  });
  if (!codeBlockEntry) return;

  const lines = getCodeBlockLines(editor, codeBlockEntry[1]);

  // Find lines that intersect with the current selection
  const selectedLines = lines.filter((line) => {
    const selection = editor.selection!;

    // For empty lines (where start and end are the same), we need special handling
    if (Point.equals(line.startPoint, line.endPoint)) {
      // Check if the cursor is exactly at this empty line position
      if (
        Range.isCollapsed(selection) &&
        Point.equals(selection.anchor, line.startPoint)
      ) {
        return true;
      }

      // Also check if the selection includes this point
      if (Range.includes(selection, line.startPoint)) {
        return true;
      }
    }

    const lineRange: Range = {
      anchor: line.startPoint,
      focus: line.endPoint,
    };
    const intersection = Range.intersection(selection, lineRange);

    // If there's an intersection, check if it's meaningful
    if (intersection) {
      // If the selection ends exactly at the start of a line (after a newline),
      // don't include that line in the indentation
      const selectionEnd = Range.isForward(selection)
        ? selection.focus
        : selection.anchor;
      const lineStart = line.startPoint;

      // If selection ends at the start of this line, exclude it
      if (Point.equals(selectionEnd, lineStart)) return false;

      return true;
    }

    return false;
  });

  // Apply indentation to selected lines in reverse order to maintain path validity
  selectedLines.reverse().forEach((line) => {
    if (direction === 'indent') {
      // Insert tab at the beginning of the line
      Transforms.insertText(editor, '\t', { at: line.startPoint });
    } else {
      // Remove leading whitespace (tab or up to 2 spaces)
      const lineContent = line.content;
      if (lineContent.startsWith('\t')) {
        Transforms.delete(editor, {
          at: line.startPoint,
          distance: 1,
        });
      } else if (lineContent.startsWith('  ')) {
        Transforms.delete(editor, {
          at: line.startPoint,
          distance: 2,
        });
      } else if (lineContent.startsWith(' ')) {
        Transforms.delete(editor, {
          at: line.startPoint,
          distance: 1,
        });
      }
    }
  });
}

// Indent all lines in the selection by one tab.
class ToolIndentCode extends Tool {
  static toolName = 'Indent';
  static iconClass = 'fa-regular fa-indent';
  static shortcut = 'Cmd+]';

  static action({ editor }: ToolArgs): void {
    applyIndent(editor, 'indent');
  }

  static isEnabled({ editor }: ToolArgs): boolean {
    return !!editor.selection && BlockUtils.selectionIsInBlock(editor, 'code');
  }
}

// Outdent all lines in the selection by one tab or two spaces.
class ToolOutdentCode extends Tool {
  static toolName = 'Outdent';
  static iconClass = 'fa-regular fa-outdent';
  static shortcut = 'Cmd+[';

  static action({ editor }: ToolArgs): void {
    applyIndent(editor, 'outdent');
  }

  static isEnabled({ editor }: ToolArgs): boolean {
    return !!editor.selection && BlockUtils.selectionIsInBlock(editor, 'code');
  }
}

export { ToolIndentCode, ToolOutdentCode };
